/**
 * @file: 层板拖拽落位任务队列构建
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../../cubeTree/index';
import * as UIToolkit from '../../../UIToolkit/index';
import * as Toolkit from '../../../toolkit/index';
import CodiSystem from '../../../codiSystem/index';

interface PhysicsMovePath {
    from: PhysicsCoordinate,
    to: PhysicsCoordinate
}

function makeMoveTask(dragGoods: CubeId[], transfer: Transfer, shelfsCoordinates: CubePaintCoordinate[]): MoveTask {
    let result: MoveTask = {
        logic: [],
        physics: []
    };

    result.logic = makeLogicTask(dragGoods, transfer, shelfsCoordinates);
    result.physics = makePhysicsTask(dragGoods, transfer, shelfsCoordinates);
    layerBootomRectify(result);
    
    return result;
}

// 如果移动对象是货架中的层板，并且货架设置了打孔值，则需要做y值修正
function layerBootomRectify(task: MoveTask): void {
    task.logic.forEach((logicTask: LogicMoveTask): void => {
        if (!CubeTree.isShelf(logicTask.to.parent)) {
            return;
        }

        task.physics.forEach((physicsTask: PhysicsMoveTask): void => {
            if (physicsTask.to.moveTarget !== logicTask.to.moveTarget) {
                return;
            }

            physicsTask.to.position.bottom = UIToolkit.layerYRectify(logicTask.to.parent, physicsTask.to.position.bottom);
        });
    });
}

function makePhysicsTask(layers: CubeId[], transfer: Transfer, shelfsCoordinates: CubePaintCoordinate[]): PhysicsMoveTask[] {
    let result: PhysicsMoveTask[] = [];

    layers.forEach((layer: CubeId): void => {
        let coordinate: CubePaintCoordinate = transfer.getTargetCubePaintCoordinate(layer);
        let physicsMovePath: PhysicsMovePath = makePhysicsMovePath(transfer, coordinate, shelfsCoordinates);

        result.push({
            from: {
                moveTarget: coordinate.id,
                position: {
                    left: physicsMovePath.from.left,
                    bottom: physicsMovePath.from.bottom
                }
            },
            to: {
                moveTarget: coordinate.id,
                position: {
                    left: physicsMovePath.to.left,
                    bottom: physicsMovePath.to.bottom
                }
            }
        });
    });

    return result;
}

function makeLogicTask(dragCubes: CubeId[], transfer: Transfer, shelfsCoordinates: CubePaintCoordinate[]): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];

    dragCubes.forEach((layer: CubeId): void => {
        let layerPaintCoordinate: CubePaintCoordinate = transfer.getTargetCubePaintCoordinate(layer);
        let layerRect: Rect = {
            left: layerPaintCoordinate.left,
            top: layerPaintCoordinate.top,
            width: layerPaintCoordinate.width,
            height: layerPaintCoordinate.height
        };
        let layerDropTarget: CubeId = findLayerDropTarget(layerRect, shelfsCoordinates);
        let task: LogicMoveTask|null = makeLayerMoveLogicTask(layer, layerDropTarget);

        task && result.push(task);
    });

    return result;
}

function findLayerDropTarget(layerRect: Rect, shelfsCoordinates: CubePaintCoordinate[]): CubeId {
    for(let i = 0; i < shelfsCoordinates.length; i++) {
        let shelfRect: Rect = {
            left: shelfsCoordinates[i].left,
            top: shelfsCoordinates[i].top,
            width: shelfsCoordinates[i].width,
            height: shelfsCoordinates[i].height
        };

        if (Toolkit.beContainHalf(layerRect, shelfRect)) {
            return shelfsCoordinates[i].id;
        }
    }

    return CubeTree.findSketchpad();
}

function makeLayerMoveLogicTask(layer: CubeId, target: CubeId): LogicMoveTask|null {
    let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(layer);

    return {
        from: {
            parent: oldParent,
            moveTarget: layer,
            before: null
        },
        to: {
            parent: target,
            moveTarget: layer,
            insertBefore: null
        }
    };
}

function makePhysicsMovePath(transfer: Transfer, layerCoordinate: CubePaintCoordinate, shelfsCoordinates: CubePaintCoordinate[]): PhysicsMovePath {
    let layerRect: Rect = {
        left: layerCoordinate.left,
        top: layerCoordinate.top,
        width: layerCoordinate.width,
        height: layerCoordinate.height
    };
    let layerDropTarget: CubeId = findLayerDropTarget(layerRect, shelfsCoordinates);
    let from: PhysicsCoordinate = CodiSystem.getRelativePhysicsCoordinate(layerCoordinate.id);
    let to: PhysicsCoordinate;

    if (CubeTree.isSketchpad(layerDropTarget)) {
        to = CodiSystem.paintToPhysics({
            left: layerCoordinate.left,
            top: layerCoordinate.top,
            width: layerCoordinate.width,
            height: layerCoordinate.height
        });
    }
    else {
        let base: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layerDropTarget);
        let target: PhysicsCoordinate = CodiSystem.paintToPhysics({
            left: layerCoordinate.left,
            top: layerCoordinate.top,
            width: layerCoordinate.width,
            height: layerCoordinate.height
        });
        let relative: PhysicsCoordinate = CodiSystem.getPhysicsRelative(target, base);

        to = {
            left: Math.round(relative.left),
            bottom: relative.bottom,
            width: relative.width,
            height: relative.height
        };
    }

    return {
        to,
        from
    };
}

export {
    makeMoveTask
};
